import { FormEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface UsuarioLinha {
  id: number;
  nome: string;
  email: string;
  telefone: string | null;
  whatsapp: string | null;
  valor_hora?: number | null;
  pode_editar_inicio_os?: boolean;
  pode_ver_localizacao_tecnico?: boolean;
  pode_ver_materiais_os?: boolean;
  pode_adicionar_obs_conclusao_os?: boolean;
  pode_editar_obs_conclusao_os?: boolean;
  tecnico_nao_pode_criar_os?: boolean;
  tecnico_busca_os_do_dia_habilitado?: boolean;
  pode_transferir_os_whatsapp?: boolean;
  pode_encaminhar_assinatura_whatsapp?: boolean;
  pode_cadastrar_material?: boolean;
  perfil: string;
  status: string;
  todas_unidades?: boolean;
  unidades?: number[];
}

interface UnidadeOpc {
  id: number;
  nome: string;
}

interface Empresa {
  id: number;
  nome: string;
  status: string;
  db_nome: string | null;
  rastreamento_tecnico_habilitado?: boolean;
  quantidade_usuarios_permitidos?: number | null;
  criacao_os_simplificada_habilitado?: boolean;
  retirar_unidade_local_piso_habilitado?: boolean;
}

interface ConfirmacaoStatusUsuario {
  usuario: UsuarioLinha;
  proximoStatus: 'ativo' | 'inativo';
}

const PERFIS: Record<string, string> = {
  admin_empresa: 'Gestor da empresa',
  supervisor: 'Supervisor',
  tecnico: 'Técnico',
  solicitante: 'Solicitante',
};

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoeda = (v: unknown): string => num(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const valorMoeda = (v: unknown): string => 'R$ ' + fmtMoeda(v);

const parseMoedaInput = (raw: string): number => {
  const digitos = raw.replace(/\D/g, '');
  return digitos ? Number(digitos) / 100 : 0;
};

function erro(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function perfilLabel(perfil: string): string {
  return PERFIS[perfil] || perfil;
}

function limiteUsuariosNormalizado(v: unknown): number | null {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function formInicial(): Partial<UsuarioLinha> & { senha?: string } {
  return {
    perfil: 'solicitante',
    status: 'ativo',
    valor_hora: 0,
    todas_unidades: false,
    unidades: [],
    pode_ver_materiais_os: true,
    pode_adicionar_obs_conclusao_os: true,
    pode_transferir_os_whatsapp: true,
    pode_encaminhar_assinatura_whatsapp: false,
  };
}

export default function Usuarios() {
  const { user } = useAuth();
  const isAdminGeral = user?.perfil === 'admin_geral';
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresaId, setEmpresaId] = useState('');
  const [lista, setLista] = useState<UsuarioLinha[]>([]);
  const [unidades, setUnidades] = useState<UnidadeOpc[]>([]);
  const [form, setForm] = useState<Partial<UsuarioLinha> & { senha?: string }>(formInicial);
  const [erroMsg, setErroMsg] = useState('');
  const [msg, setMsg] = useState('');
  const [confirmacaoStatus, setConfirmacaoStatus] = useState<ConfirmacaoStatusUsuario | null>(null);

  const editando = Boolean(form.id);
  const tecnicoSelecionado = (form.perfil || 'solicitante') === 'tecnico';
  const empresaSelecionada = isAdminGeral ? empresas.find((item) => String(item.id) === empresaId) : null;
  // Permissao "CRIACAO DE OS SIMPLIFICADA": nesse fluxo a OS nao tem unidade
  // (os/os_criar.php aceita unidade_id nulo e nem checa o vinculo do tecnico),
  // entao amarrar tecnico a unidade nao decide mais nada - o campo sai da tela.
  // Lido da empresa SELECIONADA, nao do usuario logado: o admin_geral administra
  // usuarios de outras empresas por aqui.
  const osSimplificadaEmpresaHabilitada = Boolean(
    isAdminGeral ? empresaSelecionada?.criacao_os_simplificada_habilitado : user?.criacao_os_simplificada_habilitado,
  ) || Boolean(
    // "RETIRAR UNIDADE - LOCAL - PISO": sem unidade no sistema, amarrar tecnico a
    // unidade nao decide nada - o bloco sai da tela pelo mesmo motivo.
    isAdminGeral ? empresaSelecionada?.retirar_unidade_local_piso_habilitado : user?.retirar_unidade_local_piso_habilitado,
  );
  const rastreamentoEmpresaHabilitado = Boolean(
    isAdminGeral ? empresaSelecionada?.rastreamento_tecnico_habilitado : user?.rastreamento_tecnico_habilitado,
  );

  const empresaQuery = isAdminGeral ? '?empresa_id=' + empresaId : '';
  const empresaBody = isAdminGeral ? { empresa_id: Number(empresaId) } : {};
  const podeCarregar = !isAdminGeral || Boolean(empresaId);
  const limiteUsuarios = limiteUsuariosNormalizado(
    isAdminGeral ? empresaSelecionada?.quantidade_usuarios_permitidos : user?.quantidade_usuarios_permitidos,
  );
  const totalUsuariosAtivos = lista.filter((item) => item.status === 'ativo').length;
  const limiteAtingido = !editando && limiteUsuarios !== null && totalUsuariosAtivos >= limiteUsuarios;
  const mensagemLimiteUsuarios =
    'Para adicionar mais um usuário, contrate mais usuários. Por favor, entre em contato com o suporte da Manux.';

  async function carregarEmpresas() {
    if (!isAdminGeral) return;
    const dados = await api<Empresa[]>('/empresas/empresas.php');
    setEmpresas(dados);
    if (!empresaId && dados.length) setEmpresaId(String(dados[0].id));
  }

  async function carregar() {
    setErroMsg('');
    if (!podeCarregar) {
      setLista([]);
      return;
    }

    setLista(await api<UsuarioLinha[]>('/usuarios/usuarios.php' + empresaQuery));
    if (osSimplificadaEmpresaHabilitada) {
      setUnidades([]); // campo escondido: nao vale gastar uma chamada
      return;
    }
    try {
      setUnidades(await api<UnidadeOpc[]>('/cadastros/unidades.php' + empresaQuery));
    } catch {
      setUnidades([]);
    }
  }

  async function editar(usuario: UsuarioLinha) {
    try {
      const full = await api<UsuarioLinha>('/usuarios/usuarios.php?id=' + usuario.id + (isAdminGeral ? '&empresa_id=' + empresaId : ''));
      setForm({
        ...full,
        senha: '',
        valor_hora: num(full.valor_hora),
        unidades: full.unidades || [],
        pode_ver_materiais_os: full.pode_ver_materiais_os ?? true,
        pode_adicionar_obs_conclusao_os: full.pode_adicionar_obs_conclusao_os ?? true,
        pode_transferir_os_whatsapp: full.pode_transferir_os_whatsapp ?? true,
        pode_encaminhar_assinatura_whatsapp: full.pode_encaminhar_assinatura_whatsapp ?? false,
        pode_cadastrar_material: full.pode_cadastrar_material ?? false,
      });
    } catch {
      setForm({
        ...usuario,
        senha: '',
        valor_hora: num(usuario.valor_hora),
        pode_ver_materiais_os: usuario.pode_ver_materiais_os ?? true,
        pode_adicionar_obs_conclusao_os: usuario.pode_adicionar_obs_conclusao_os ?? true,
        pode_transferir_os_whatsapp: usuario.pode_transferir_os_whatsapp ?? true,
        pode_encaminhar_assinatura_whatsapp: usuario.pode_encaminhar_assinatura_whatsapp ?? false,
        pode_cadastrar_material: usuario.pode_cadastrar_material ?? false,
      });
    }
  }

  useEffect(() => {
    carregarEmpresas().catch((e) => setErroMsg(erro(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminGeral]);

  useEffect(() => {
    carregar().catch((e) => setErroMsg(erro(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, isAdminGeral]);

  function limparForm() {
    setForm(formInicial());
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (isAdminGeral && !empresaId) {
      setErroMsg('Selecione a empresa antes de salvar.');
      return;
    }
    if (!editando && limiteAtingido) {
      setErroMsg(mensagemLimiteUsuarios);
      window.alert(mensagemLimiteUsuarios);
      return;
    }

    setErroMsg('');
    setMsg('');

    const body: Record<string, unknown> = {
      ...empresaBody,
      id: form.id,
      nome: (form.nome || '').trim(),
      telefone: form.telefone || null,
      whatsapp: form.whatsapp || null,
      valor_hora: tecnicoSelecionado ? num(form.valor_hora) : null,
      pode_editar_inicio_os: Boolean(form.pode_editar_inicio_os),
      pode_ver_localizacao_tecnico: rastreamentoEmpresaHabilitado && Boolean(form.pode_ver_localizacao_tecnico),
      pode_ver_materiais_os: form.pode_ver_materiais_os !== false,
      pode_adicionar_obs_conclusao_os: form.pode_adicionar_obs_conclusao_os !== false,
      pode_editar_obs_conclusao_os: Boolean(form.pode_editar_obs_conclusao_os),
      tecnico_nao_pode_criar_os: Boolean(form.tecnico_nao_pode_criar_os),
      tecnico_busca_os_do_dia_habilitado: Boolean(form.tecnico_busca_os_do_dia_habilitado),
      pode_transferir_os_whatsapp: form.pode_transferir_os_whatsapp !== false,
      pode_encaminhar_assinatura_whatsapp: Boolean(form.pode_encaminhar_assinatura_whatsapp),
      pode_cadastrar_material: Boolean(form.pode_cadastrar_material),
      perfil: form.perfil || 'solicitante',
      status: form.status || 'ativo',
      todas_unidades: osSimplificadaEmpresaHabilitada ? true : Boolean(form.todas_unidades),
      unidades: osSimplificadaEmpresaHabilitada || form.todas_unidades ? [] : (form.unidades || []),
    };

    body.email = (form.email || '').trim();
    if (!editando) {
      body.senha = form.senha || '';
    } else if (form.senha) {
      body.senha = form.senha;
    }

    try {
      if (form.id) await api('/usuarios/usuarios.php', { method: 'PUT', body });
      else await api('/usuarios/usuarios.php', { method: 'POST', body });
      setMsg(editando ? 'Permissão do usuário atualizada.' : 'Usuário criado.');
      limparForm();
      await carregar();
      if (isAdminGeral) await carregarEmpresas();
    } catch (e2) {
      setErroMsg(erro(e2));
    }
  }

  function abrirConfirmacaoStatus(usuario: UsuarioLinha) {
    setConfirmacaoStatus({
      usuario,
      proximoStatus: usuario.status === 'ativo' ? 'inativo' : 'ativo',
    });
  }

  async function alternarStatus(usuario: UsuarioLinha, proximoStatus: 'ativo' | 'inativo') {
    setErroMsg('');
    setMsg('');
    try {
      await api('/usuarios/usuarios.php', {
        method: 'PUT',
        body: {
          ...empresaBody,
          id: usuario.id,
          status: proximoStatus,
        },
      });
      setMsg(proximoStatus === 'ativo' ? 'Usuário reativado.' : 'Usuário desativado.');
      await carregar();
      if (isAdminGeral) await carregarEmpresas();
    } catch (e) {
      setErroMsg(erro(e));
    }
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Usuários e permissões</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErroMsg(erro(e)))}>
          Atualizar
        </button>
      </div>

      {erroMsg && <div className="msg-erro">{erroMsg}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {isAdminGeral && (
        <div className="card" style={{ marginBottom: 16 }}>
          <label>Empresa</label>
          <select
            value={empresaId}
            onChange={(e) => {
              setEmpresaId(e.target.value);
              limparForm();
            }}
          >
            <option value="">Selecione</option>
            {empresas.map((empresa) => (
              <option key={empresa.id} value={empresa.id}>
                {empresa.nome} - {empresa.status}
              </option>
            ))}
          </select>
        </div>
      )}

      <form className="card" onSubmit={salvar} style={{ marginBottom: 16 }}>
        <h3>{editando ? 'Editar permissão' : 'Novo usuário'}</h3>

        {limiteUsuarios !== null && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 14px',
              borderRadius: 12,
              border: `1px solid ${limiteAtingido ? '#fed7aa' : '#dbeafe'}`,
              background: limiteAtingido ? '#fff7ed' : '#f8fbff',
              color: limiteAtingido ? '#9a3412' : '#0a49c2',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {totalUsuariosAtivos} de {limiteUsuarios} usuários em uso.
            {limiteAtingido ? ' Limite atingido para esta empresa.' : ' Ainda há vagas disponíveis para novos usuários.'}
          </div>
        )}

        <div className="grid cols2">
          <div>
            <label>Nome *</label>
            <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
          </div>
          <div>
            <label>E-mail *</label>
            <input
              className="input"
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label>Permissão</label>
            <select
              value={form.perfil || 'solicitante'}
              onChange={(e) =>
                setForm({
                  ...form,
                  perfil: e.target.value,
                  valor_hora: e.target.value === 'tecnico' ? form.valor_hora || 0 : 0,
                  todas_unidades: e.target.value === 'admin_empresa' ? true : Boolean(form.todas_unidades),
                  unidades: e.target.value === 'admin_empresa' ? [] : (form.unidades || []),
                  pode_editar_inicio_os: e.target.value === 'tecnico' ? form.pode_editar_inicio_os : false,
                  pode_editar_obs_conclusao_os: e.target.value === 'tecnico' ? form.pode_editar_obs_conclusao_os : false,
                  tecnico_nao_pode_criar_os: e.target.value === 'tecnico' ? form.tecnico_nao_pode_criar_os : false,
                  tecnico_busca_os_do_dia_habilitado: e.target.value === 'tecnico' ? form.tecnico_busca_os_do_dia_habilitado : false,
                  pode_transferir_os_whatsapp: e.target.value === 'tecnico' ? (form.pode_transferir_os_whatsapp ?? true) : true,
                  pode_encaminhar_assinatura_whatsapp: e.target.value === 'tecnico' ? Boolean(form.pode_encaminhar_assinatura_whatsapp) : false,
                  pode_cadastrar_material: e.target.value === 'tecnico' ? Boolean(form.pode_cadastrar_material) : false,
                })
              }
            >
              {Object.entries(PERFIS).map(([valor, label]) => (
                <option key={valor} value={valor}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Status</label>
            <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
          <div>
            <label>Valor da Hora (R$)</label>
            <input
              className="input"
              inputMode="numeric"
              value={valorMoeda(form.valor_hora)}
              onChange={(e) => setForm({ ...form, valor_hora: parseMoedaInput(e.target.value) })}
              disabled={!tecnicoSelecionado}
            />
          </div>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_ver_materiais_os ?? true)}
              onChange={(e) => setForm({ ...form, pode_ver_materiais_os: e.target.checked })}
            />
            <span>Visualizar materiais da OS</span>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_adicionar_obs_conclusao_os ?? true)}
              onChange={(e) => setForm({ ...form, pode_adicionar_obs_conclusao_os: e.target.checked })}
            />
            <span>Adicionar observação na conclusão da OS</span>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_editar_inicio_os)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, pode_editar_inicio_os: e.target.checked })}
            />
            <span>Técnico pode editar início e fim da OS</span>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_editar_obs_conclusao_os)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, pode_editar_obs_conclusao_os: e.target.checked })}
            />
            <span>Técnico pode editar observação de conclusão mesmo com a OS já encerrada</span>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.tecnico_nao_pode_criar_os)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, tecnico_nao_pode_criar_os: e.target.checked })}
            />
            <span>Técnico não pode criar OS</span>
            <small style={{ color: 'var(--texto-suave)', display: 'block', marginTop: 4 }}>
              Bloqueia a criação de OS pelo técnico na tela e no aplicativo (a API também recusa o pedido).
            </small>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.tecnico_busca_os_do_dia_habilitado)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, tecnico_busca_os_do_dia_habilitado: e.target.checked })}
            />
            <span>Buscar OS do dia no aplicativo</span>
            <small style={{ color: 'var(--texto-suave)', display: 'block', marginTop: 4 }}>
              Ligado, a lista de OS do app mobile já abre filtrada para o dia atual. Desligado, abre sem filtro de data (o técnico ainda pode filtrar manualmente).
            </small>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_transferir_os_whatsapp ?? true)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, pode_transferir_os_whatsapp: e.target.checked })}
            />
            <span>Técnico pode transferir OS para WhatsApp</span>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_encaminhar_assinatura_whatsapp)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, pode_encaminhar_assinatura_whatsapp: e.target.checked })}
            />
            <span>Tecnico pode encaminhar assinatura via WhatsApp</span>
          </label>
          <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(form.pode_cadastrar_material)}
              disabled={!tecnicoSelecionado}
              onChange={(e) => setForm({ ...form, pode_cadastrar_material: e.target.checked })}
            />
            <span>Técnico pode cadastrar material</span>
            <small style={{ color: 'var(--texto-suave)', display: 'block', marginTop: 4 }}>
              Libera a tela Estoque (cadastrar, editar e desativar material) para o técnico.
            </small>
          </label>
          {rastreamentoEmpresaHabilitado && (
            <label className="check-line" style={{ alignSelf: 'end', marginBottom: 10 }}>
              <input
                type="checkbox"
                checked={Boolean(form.pode_ver_localizacao_tecnico)}
                onChange={(e) => setForm({ ...form, pode_ver_localizacao_tecnico: e.target.checked })}
              />
              <span>Ver localização do técnico</span>
              <small style={{ color: 'var(--texto-suave)', display: 'block', marginTop: 4 }}>
                Controla a coleta da localizacao do tecnico no navegador e o acesso ao rastreamento.
              </small>
            </label>
          )}
          {!osSimplificadaEmpresaHabilitada && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 700 }}>Acesso às unidades</label>
            <label className="check-line">
              <input
                type="checkbox"
                checked={Boolean(form.todas_unidades)}
                onChange={(e) => setForm({ ...form, todas_unidades: e.target.checked })}
              />
              <span>Todas as unidades, inclusive futuras</span>
            </label>
            {!form.todas_unidades && (
              <div className="check-grid" style={{ marginTop: 6 }}>
                {unidades.map((unidade) => {
                  const selecionada = (form.unidades || []).includes(unidade.id);
                  return (
                    <label key={unidade.id} className="check-line">
                      <input
                        type="checkbox"
                        checked={selecionada}
                        onChange={() => {
                          const atual = form.unidades || [];
                          setForm({
                            ...form,
                            unidades: selecionada ? atual.filter((item) => item !== unidade.id) : [...atual, unidade.id],
                          });
                        }}
                      />
                      {unidade.nome}
                    </label>
                  );
                })}
                {!unidades.length && <span className="vazio">Nenhuma unidade cadastrada.</span>}
              </div>
            )}
          </div>
          )}
          <div>
            <label>{editando ? 'Nova senha (opcional)' : 'Senha *'}</label>
            <input
              className="input"
              type="password"
              value={form.senha || ''}
              onChange={(e) => setForm({ ...form, senha: e.target.value })}
              required={!editando}
            />
          </div>
          <div>
            <label>WhatsApp</label>
            <input className="input" value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
          </div>
          <div>
            <label>Telefone</label>
            <input className="input" value={form.telefone || ''} onChange={(e) => setForm({ ...form, telefone: e.target.value })} />
          </div>
        </div>

        <div className="form-actions">
          {editando && (
            <button type="button" className="btn sec" onClick={limparForm}>
              Cancelar
            </button>
          )}
          <button className={`btn${limiteAtingido && !editando ? ' btn-bloqueado-claro' : ''}`} disabled={isAdminGeral && !empresaId}>
            {editando ? 'Salvar permissão' : 'Adicionar usuário'}
          </button>
        </div>
      </form>

      <div className="tabela-wrap">
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Permissão</th>
              <th>Valor/hora</th>
              {rastreamentoEmpresaHabilitado && <th>Localizacao autorizada</th>}
              <th>Contato</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lista.map((usuario) => (
              <tr key={usuario.id}>
                <td>
                  <strong>{usuario.nome}</strong>
                </td>
                <td>{usuario.email}</td>
                <td>{perfilLabel(usuario.perfil)}</td>
                <td>{usuario.perfil === 'tecnico' ? valorMoeda(usuario.valor_hora || 0) : '-'}</td>
                {rastreamentoEmpresaHabilitado && <td>{usuario.pode_ver_localizacao_tecnico ? 'Sim' : '-'}</td>}
                <td>{usuario.whatsapp || usuario.telefone || '-'}</td>
                <td>
                  <span className="badge">{usuario.status}</span>
                </td>
                <td className="acoes">
                  <button className="btn sec sm" onClick={() => editar(usuario)}>
                    Editar permissão
                  </button>
                  <button className={`btn ${usuario.status === 'ativo' ? 'danger' : 'sec'} sm`} onClick={() => abrirConfirmacaoStatus(usuario)}>
                    {usuario.status === 'ativo' ? 'Desativar' : 'Ativar'}
                  </button>
                </td>
              </tr>
            ))}
            {!lista.length && (
              <tr>
                <td colSpan={rastreamentoEmpresaHabilitado ? 8 : 7} className="vazio">
                  Nenhum usuário cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmacaoStatus &&
        createPortal(
          <div
            onClick={() => setConfirmacaoStatus(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(11,31,77,.55)',
              backdropFilter: 'blur(3px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              zIndex: 2000,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff',
                borderRadius: 20,
                padding: '24px 24px 18px',
                width: '100%',
                maxWidth: 460,
                boxShadow: '0 30px 70px rgba(11,31,77,.35)',
                border: '1px solid #e7ecf5',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <span
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 14,
                    background: confirmacaoStatus.proximoStatus === 'ativo' ? '#dcfce7' : '#fee2e2',
                    color: confirmacaoStatus.proximoStatus === 'ativo' ? '#15803d' : '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {confirmacaoStatus.proximoStatus === 'ativo' ? '<' : '!'}
                </span>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>
                    {confirmacaoStatus.proximoStatus === 'ativo' ? 'Reativar usuário' : 'Desativar usuário'}
                  </h3>
                  <div style={{ marginTop: 4, color: 'var(--texto-suave)', fontSize: 13 }}>{confirmacaoStatus.usuario.nome}</div>
                </div>
              </div>

              <div style={{ color: 'var(--texto-suave)', fontSize: 14, lineHeight: 1.65 }}>
                {confirmacaoStatus.proximoStatus === 'ativo'
                  ? 'Este usuário voltará a ter acesso ao sistema, desde que a empresa ainda tenha vaga no limite de usuários ativos.'
                  : 'Este usuário perderá o acesso ao sistema, mas o histórico e os registros vinculados serão preservados.'}
              </div>

              <div className="form-actions" style={{ marginTop: 18 }}>
                <button type="button" className="btn sec" onClick={() => setConfirmacaoStatus(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className={`btn ${confirmacaoStatus.proximoStatus === 'ativo' ? '' : 'danger'}`}
                  style={{ boxShadow: 'none' }}
                  onClick={() => {
                    const atual = confirmacaoStatus;
                    setConfirmacaoStatus(null);
                    void alternarStatus(atual.usuario, atual.proximoStatus);
                  }}
                >
                  {confirmacaoStatus.proximoStatus === 'ativo' ? 'Confirmar reativação' : 'Confirmar desativação'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
